/**
 * WorkspaceEditor — single-page editor for a workspace draft. Used both for
 * "create new" and "edit existing".
 *
 * Layout follows the project's admin standard (px-6 py-6 space-y-6) and uses
 * sectioned cards rather than a stepped wizard so admins can scrub freely.
 *
 * Live preview is intentionally minimal: we render the resolved nav list +
 * widget tiles in a side panel rather than firing up a sandboxed Layout. The
 * full in-app preview is provided by `previewWorkspace()` on the workspace
 * provider — which the admin invokes via the "Preview live" button (sets the
 * workspace context to this draft for the rest of the session, until they hit
 * "Stop preview").
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useWorkspace, type ResolvedNavItem } from "@/workspace/WorkspaceProvider";
import {
  ALLOWED_LANDING_PATHS,
  DASHBOARD_WIDGET_REGISTRY,
  NAV_ITEM_REGISTRY,
  NAV_ITEM_IDS,
  WIDGET_IDS,
  PAGE_DEFAULT_PATHS,
  type NavItemEntry,
} from "@/workspace/registry";
import { workspaceViewDraftSchema } from "@/workspace/schema";
import { groupWorkspaceNav } from "@/components/AppSidebar";
import {
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
} from "@/workspace/cloud";
import type { WorkspaceView, WorkspaceViewDraft } from "@/workspace/types";
import { COPY_PRESETS } from "@/theme/copy/presets";
import { getCurrentTenantId } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  initial: WorkspaceView | null; // null => create mode
}

const COPY_PRESET_OPTIONS = Object.values(COPY_PRESETS).map((p) => ({
  id: p.id,
  name: p.name,
  description: p.description,
}));

function emptyDraft(): WorkspaceViewDraft {
  return {
    name: "",
    description: null,
    roleName: null,
    isDefault: true,
    landingPath: "/getting-started",
    navItems: [],
    dashboardWidgets: [],
    pageDefaults: {},
    copyPresetId: null,
  };
}

function fromWorkspace(ws: WorkspaceView): WorkspaceViewDraft {
  return {
    name: ws.name,
    description: ws.description,
    roleName: ws.roleName,
    isDefault: ws.isDefault,
    landingPath: ws.landingPath,
    navItems: ws.navItems,
    dashboardWidgets: ws.dashboardWidgets,
    pageDefaults: ws.pageDefaults,
    copyPresetId: ws.copyPresetId,
  };
}

export function WorkspaceEditor({ initial }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { previewWorkspace } = useWorkspace();
  const [draft, setDraft] = React.useState<WorkspaceViewDraft>(
    initial ? fromWorkspace(initial) : emptyDraft(),
  );
  const [previewing, setPreviewing] = React.useState(false);
  /**
   * Set of nav-permission resources the admin wants to "simulate hiding" in
   * the preview. Lets them see how the sidebar collapses for a role that
   * lacks specific resources, without having to actually impersonate.
   */
  const [simulateHidden, setSimulateHidden] = React.useState<Set<string>>(new Set());

  const update = <K extends keyof WorkspaceViewDraft>(key: K, value: WorkspaceViewDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const navSelected = new Set(draft.navItems.map((n) => n.id));
  const widgetSelected = new Set(draft.dashboardWidgets.map((w) => w.id));

  const toggleNav = (id: string) => {
    if (navSelected.has(id)) {
      update("navItems", draft.navItems.filter((n) => n.id !== id));
    } else {
      const order = draft.navItems.length;
      update("navItems", [...draft.navItems, { id, order }]);
    }
  };

  /**
   * Move an item up/down in the visual order. We renumber 0..n-1 so order
   * values stay dense and predictable for downstream sort keys.
   */
  const moveNav = (id: string, dir: -1 | 1) => {
    const items = [...draft.navItems].sort((a, b) => a.order - b.order);
    const idx = items.findIndex((n) => n.id === id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target], items[idx]];
    update("navItems", items.map((n, i) => ({ ...n, order: i })));
  };

  /**
   * Set an explicit order for a single nav item. The submitted value is
   * clamped to the schema range (0..999) and the remaining items are
   * compacted around it on blur so two items never end up sharing a slot —
   * which the server-side validation trigger would also reject.
   */
  const setNavOrder = (id: string, raw: number) => {
    const safe = Math.max(0, Math.min(999, Math.floor(raw)));
    const others = draft.navItems
      .filter((n) => n.id !== id)
      .sort((a, b) => a.order - b.order)
      .map((n) => n.id);
    const insertAt = Math.min(safe, others.length);
    const ordered = [...others.slice(0, insertAt), id, ...others.slice(insertAt)];
    update(
      "navItems",
      ordered.map((nid, i) => {
        const existing = draft.navItems.find((n) => n.id === nid)!;
        return { ...existing, order: i };
      }),
    );
  };

  const toggleWidget = (id: string) => {
    if (widgetSelected.has(id)) {
      update("dashboardWidgets", draft.dashboardWidgets.filter((w) => w.id !== id));
    } else {
      update("dashboardWidgets", [...draft.dashboardWidgets, { id, order: draft.dashboardWidgets.length }]);
    }
  };

  const setPageDefault = (path: string, value: string) => {
    const next = { ...draft.pageDefaults };
    if (value.trim()) next[path] = value.trim().replace(/^\?/, "");
    else delete next[path];
    update("pageDefaults", next);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const parsed = workspaceViewDraftSchema.safeParse(draft);
      if (!parsed.success) {
        const first = parsed.error.errors[0];
        throw new Error(first?.message ?? "Invalid workspace");
      }
      const tenantId = getCurrentTenantId();
      if (!tenantId) throw new Error("No tenant context — please re-sign in.");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in.");
      const validated = parsed.data as WorkspaceViewDraft;
      if (initial) {
        return updateWorkspace(initial.id, validated);
      }
      return createWorkspace(validated, tenantId, user.id);
    },
    onSuccess: () => {
      toast.success(initial ? "Workspace updated" : "Workspace created");
      queryClient.invalidateQueries({ queryKey: ["workspace_views"] });
      navigate("/admin/workspaces");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!initial) return;
      await deleteWorkspace(initial.id);
    },
    onSuccess: () => {
      toast.success("Workspace deleted");
      queryClient.invalidateQueries({ queryKey: ["workspace_views"] });
      navigate("/admin/workspaces");
    },
    onError: (err: Error) => toast.error(`Failed to delete workspace: ${err.message}`),
  });

  const startPreview = () => {
    // Build a synthetic WorkspaceView from the draft so the provider can apply it.
    const synthetic: WorkspaceView = {
      id: initial?.id ?? "preview",
      tenantId: initial?.tenantId ?? "preview",
      name: draft.name || "Preview",
      description: draft.description,
      roleName: draft.roleName,
      isDefault: draft.isDefault,
      landingPath: draft.landingPath,
      navItems: draft.navItems,
      dashboardWidgets: draft.dashboardWidgets,
      pageDefaults: draft.pageDefaults,
      copyPresetId: draft.copyPresetId,
      createdBy: "preview",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    previewWorkspace(synthetic);
    setPreviewing(true);
    toast.success("Previewing workspace — navigate around the app to test.");
  };

  const stopPreview = () => {
    previewWorkspace(null);
    setPreviewing(false);
  };

  React.useEffect(() => () => {
    // Clear any preview when leaving the editor.
    previewWorkspace(null);
  }, [previewWorkspace]);

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/workspaces")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">
              {initial ? `Edit ${initial.name}` : "New workspace"}
            </h1>
            <p className="text-sm text-muted-foreground">
              Curate the navigation, dashboard, and defaults for a role.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {previewing ? (
            <Button variant="outline" onClick={stopPreview}>
              <EyeOff className="h-4 w-4 mr-2" />
              Stop preview
            </Button>
          ) : (
            <Button variant="outline" onClick={startPreview}>
              <Eye className="h-4 w-4 mr-2" />
              Preview live
            </Button>
          )}
          {initial ? (
            <Button
              variant="ghost"
              onClick={() => {
                if (confirm(`Delete "${initial.name}"? This action cannot be undone.`)) {
                  deleteMutation.mutate();
                }
              }}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          ) : null}
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Identity</CardTitle>
              <CardDescription>Name the workspace and bind it to a role.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ws-name">Name</Label>
                <Input
                  id="ws-name"
                  value={draft.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Compliance Auditor view"
                  maxLength={80}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ws-desc">Description</Label>
                <Textarea
                  id="ws-desc"
                  value={draft.description ?? ""}
                  onChange={(e) =>
                    update("description", e.target.value.trim() ? e.target.value : null)
                  }
                  placeholder="What this view is for"
                  maxLength={280}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ws-role">Role binding</Label>
                <Input
                  id="ws-role"
                  value={draft.roleName ?? ""}
                  onChange={(e) =>
                    update("roleName", e.target.value.trim() ? e.target.value : null)
                  }
                  placeholder="e.g. Compliance Auditor"
                  maxLength={80}
                />
                <p className="text-xs text-muted-foreground">
                  Users with this role (case-insensitive match) get this workspace by default.
                </p>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Default for this role</div>
                  <div className="text-xs text-muted-foreground">
                    Only one workspace can be the default per role.
                  </div>
                </div>
                <Switch
                  checked={draft.isDefault}
                  onCheckedChange={(v) => update("isDefault", v)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ws-landing">Landing page after sign-in</Label>
                <Select
                  value={draft.landingPath}
                  onValueChange={(v) => update("landingPath", v)}
                >
                  <SelectTrigger id="ws-landing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NAV_ITEM_IDS.map((id) => {
                      const entry = NAV_ITEM_REGISTRY[id] as NavItemEntry;
                      return (
                        <SelectItem key={id} value={entry.url}>
                          {entry.title} <span className="text-muted-foreground">({entry.url})</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Navigation</CardTitle>
              <CardDescription>
                Pick which sidebar items appear and reorder them. Items the role lacks
                permission for are hidden automatically at runtime.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border divide-y">
                {NAV_ITEM_IDS.map((id) => {
                  const entry = NAV_ITEM_REGISTRY[id] as NavItemEntry;
                  const checked = navSelected.has(id);
                  const cfg = draft.navItems.find((n) => n.id === id);
                  return (
                    <div key={id} className="flex items-center gap-3 px-3 py-2">
                      <Checkbox checked={checked} onCheckedChange={() => toggleNav(id)} />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{entry.title}</div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{entry.section}</span>
                          {entry.tags.map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] py-0">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {checked && cfg ? (
                        <div className="flex items-center gap-1">
                          <Label
                            htmlFor={`order-${id}`}
                            className="text-xs text-muted-foreground"
                          >
                            Order
                          </Label>
                          <Input
                            id={`order-${id}`}
                            type="number"
                            min={0}
                            max={999}
                            value={cfg.order}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (Number.isFinite(v)) setNavOrder(id, v);
                            }}
                            className="h-8 w-16"
                            aria-label={`Sidebar order for ${entry.title}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveNav(id, -1)}
                            disabled={cfg.order === 0}
                            aria-label={`Move ${entry.title} up`}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveNav(id, 1)}
                            disabled={cfg.order === draft.navItems.length - 1}
                            aria-label={`Move ${entry.title} down`}
                          >
                            ↓
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dashboard widgets</CardTitle>
              <CardDescription>
                Choose which widgets appear on the role's dashboard at /analytics.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2">
                {WIDGET_IDS.map((id) => {
                  const entry = DASHBOARD_WIDGET_REGISTRY[id];
                  const checked = widgetSelected.has(id);
                  return (
                    <label
                      key={id}
                      className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent/30"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleWidget(id)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-medium">{entry.title}</div>
                        <div className="text-xs text-muted-foreground">{entry.description}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {entry.tags.map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] py-0">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Default filters</CardTitle>
              <CardDescription>
                Pre-fill query parameters when this role first visits a page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {PAGE_DEFAULT_PATHS.map((path) => (
                <div key={path} className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
                  <Label className="font-mono text-xs">{path}</Label>
                  <Input
                    value={draft.pageDefaults[path] ?? ""}
                    onChange={(e) => setPageDefault(path, e.target.value)}
                    placeholder="e.g. compliance=non-compliant&severity=high"
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Copy preset</CardTitle>
              <CardDescription>
                Optional: which copy preset should this workspace activate? Leave as None to
                use the tenant default.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={draft.copyPresetId ?? "__none"}
                onValueChange={(v) => update("copyPresetId", v === "__none" ? null : (v as never))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Use tenant default</SelectItem>
                  {COPY_PRESET_OPTIONS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — <span className="text-muted-foreground">{p.description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-base">Sidebar preview</CardTitle>
              <CardDescription>
                Grouped by section, in the same order the {draft.roleName?.trim() || "bound role"}{" "}
                will see at runtime.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                // Resolve nav items the same way the runtime sidebar does:
                // 1) drop unknown registry ids, 2) honor draft order, 3) hide
                // any item whose permission resource the admin is simulating
                // as missing, then 4) group via the shared `groupWorkspaceNav`.
                const resolved: ResolvedNavItem[] = [...draft.navItems]
                  .map((cfg) => {
                    const entry = NAV_ITEM_REGISTRY[cfg.id as keyof typeof NAV_ITEM_REGISTRY] as
                      | NavItemEntry
                      | undefined;
                    if (!entry) return null;
                    if (entry.permission && simulateHidden.has(entry.permission)) return null;
                    return {
                      ...entry,
                      order: cfg.order,
                      label: cfg.label?.trim() || entry.title,
                    } as ResolvedNavItem;
                  })
                  .filter((x): x is ResolvedNavItem => x !== null);
                const sections = groupWorkspaceNav(resolved);

                if (sections.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      Nothing visible — pick at least one nav item (and make sure simulated
                      permissions don't hide them all).
                    </p>
                  );
                }
                return (
                  <div className="space-y-3">
                    {sections.map((section) => (
                      <div key={section.label}>
                        <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                          {section.label}
                        </div>
                        <ul className="space-y-1">
                          {section.items.map((item) => (
                            <li
                              key={item.id}
                              className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm"
                            >
                              {item.title}
                              <div className="text-xs text-muted-foreground font-mono">
                                {item.url}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {(() => {
                // Derive the toggleable permission set from currently selected
                // nav items so the simulator never offers irrelevant resources.
                const resources = Array.from(
                  new Set(
                    draft.navItems
                      .map((cfg) => {
                        const e = NAV_ITEM_REGISTRY[cfg.id as keyof typeof NAV_ITEM_REGISTRY] as
                          | NavItemEntry
                          | undefined;
                        return e?.permission;
                      })
                      .filter((p): p is NonNullable<typeof p> => Boolean(p))
                      .map((p) => String(p)),
                  ),
                ).sort();
                if (resources.length === 0) return null;
                return (
                  <div>
                    <Separator className="mb-3" />
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                      Simulate missing permissions
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Toggle a resource to hide its nav items, mirroring how a role without that
                      permission would see the sidebar.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {resources.map((res) => {
                        const hidden = simulateHidden.has(res);
                        return (
                          <Button
                            key={res}
                            type="button"
                            variant={hidden ? "default" : "outline"}
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              setSimulateHidden((prev) => {
                                const next = new Set(prev);
                                if (next.has(res)) next.delete(res);
                                else next.add(res);
                                return next;
                              })
                            }
                            aria-pressed={hidden}
                          >
                            {hidden ? "Hidden:" : ""} {res}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <Separator />
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  Widgets ({draft.dashboardWidgets.length})
                </div>
                <ul className="text-xs text-muted-foreground space-y-0.5">
                  {[...draft.dashboardWidgets]
                    .sort((a, b) => a.order - b.order)
                    .map((cfg) => {
                      const entry =
                        DASHBOARD_WIDGET_REGISTRY[cfg.id as keyof typeof DASHBOARD_WIDGET_REGISTRY];
                      return entry ? <li key={cfg.id}>{entry.title}</li> : null;
                    })}
                </ul>
              </div>
              <div className="text-xs">
                <span className="font-semibold">Lands on:</span>{" "}
                <span className="font-mono">{draft.landingPath}</span>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
